/**
 * BuyBillsSheet -- Premium bills store bottom sheet.
 *
 * Uses a View overlay instead of Modal for reliable rendering on web + native.
 * Fully functional: real useBills() state, live coin conversion, real countdown
 * timer for the flash deal, and animated offer cards.
 *
 * Design: intentionally dark premium aesthetic (like App Store purchase sheets)
 * regardless of system theme -- this is a deliberate product choice for a
 * high-conversion purchase surface.
 */
import {useEffect, useMemo, useRef, useState} from 'react'
import {
  Animated,
  Dimensions,
  Easing,
  Keyboard,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {LinearGradient} from 'expo-linear-gradient'
import {Feather} from '@expo/vector-icons'

import {useSession} from '#/state/session'
import {DinarSymbol} from '#/screens/Dashboard/components/DinarSymbol'
import {useBills} from '#/screens/Dashboard/contexts/BillsContext'
import {type AtipayCreds} from '#/screens/Wallet/lib/atipayment'
import {AtipaySheet} from './AtipaySheet'

const {width: SW, height: SH} = Dimensions.get('window')
const COIN_PER_BILL = 100

// Flash deal target: 48 h from a fixed epoch (persists across sessions)
const DEAL_EPOCH = 1754000000000
function getFlashSecondsLeft(): number {
  const cycle = 48 * 3600 * 1000
  const elapsed = (Date.now() - DEAL_EPOCH) % cycle
  return Math.max(0, Math.floor((cycle - elapsed) / 1000))
}

interface Props {
  visible: boolean
  onClose: () => void
  showDailyFreeButton?: boolean
}

// ── Offer definitions ─────────────────────────────────────────────────────────
const OFFERS = [
  {
    id: 'starter',
    bills: 5,
    priceNum: 15,
    gradient: ['#003d7a', '#001f3f'] as const,
    accentColor: '#0085ff',
    emoji: '💵',
    badge: null as string | null,
    title: 'Starter',
    tagline: 'Essaie sans engagement',
    features: ['5 capsules jouees', 'Tous les themes', 'Stats de base'],
    social: '340 etudiants',
  },
  {
    id: 'boost',
    bills: 15,
    priceNum: 30,
    gradient: ['#4a0080', '#220040'] as const,
    accentColor: '#a855f7',
    emoji: '🚀',
    badge: '⭐ POPULAIRE',
    title: 'Boost',
    tagline: 'Le choix des meilleurs',
    features: [
      '15 capsules jouees',
      'Contenu exclusif',
      'Badges rares',
      '-33% vs Starter',
    ],
    social: '1 204 etudiants',
  },
  {
    id: 'pro',
    bills: 30,
    priceNum: 55,
    gradient: ['#064e1e', '#032410'] as const,
    accentColor: '#22c55e',
    emoji: '⚡',
    badge: null,
    title: 'Pro',
    tagline: 'Pour les apprenants serieux',
    features: [
      '30 capsules jouees',
      'Modules exclusifs',
      'Analyses avancees',
      'Support prioritaire',
    ],
    social: '728 etudiants',
  },
  {
    id: 'ultime',
    bills: 100,
    priceNum: 150,
    gradient: ['#7a4500', '#3d2200'] as const,
    accentColor: '#f59e0b',
    emoji: '⭐',
    badge: '🔥 MEILLEURE VALEUR',
    title: 'Ultime',
    tagline: 'Acces illimite au reseau',
    features: [
      '100 capsules jouees',
      '-50% vs Starter',
      'Acces createur',
      'Badge Ultime exclusif',
      '+500 Coins bonus 🪙',
    ],
    social: '289 etudiants',
  },
]

// ── Shimmer ───────────────────────────────────────────────────────────────────
function Shimmer({
  width,
  height,
  borderRadius = 8,
}: {
  width: number
  height: number
  borderRadius?: number
}) {
  const anim = useRef(new Animated.Value(0)).current
  useEffect(() => {
    Animated.loop(
      Animated.timing(anim, {
        toValue: 1,
        duration: 1800,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    ).start()
  }, [])
  const translateX = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [-width, width],
  })
  return (
    <View
      style={{
        width,
        height,
        borderRadius,
        overflow: 'hidden',
        backgroundColor: 'rgba(255,255,255,0.04)',
        position: 'absolute',
      }}>
      <Animated.View
        style={{...StyleSheet.absoluteFillObject, transform: [{translateX}]}}>
        <LinearGradient
          colors={['transparent', 'rgba(255,255,255,0.07)', 'transparent']}
          start={{x: 0, y: 0}}
          end={{x: 1, y: 0}}
          style={StyleSheet.absoluteFillObject}
        />
      </Animated.View>
    </View>
  )
}

// ── Countdown display ─────────────────────────────────────────────────────────
function Countdown({seconds}: {seconds: number}) {
  const h = Math.floor(seconds / 3600)
    .toString()
    .padStart(2, '0')
  const m = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, '0')
  const s = (seconds % 60).toString().padStart(2, '0')
  return (
    <View style={{alignItems: 'center'}}>
      <Text
        style={{
          color: '#ef4444',
          fontSize: 16,
          fontWeight: '900',
          letterSpacing: 1,
        }}>
        {h}:{m}:{s}
      </Text>
      <Text style={{color: 'rgba(255,255,255,0.4)', fontSize: 9, marginTop: 2}}>
        restant
      </Text>
    </View>
  )
}

// ── Offer Card ────────────────────────────────────────────────────────────────
function OfferCard({
  offer,
  onBuy,
  isPopular,
}: {
  offer: (typeof OFFERS)[0]
  onBuy: () => void
  isPopular?: boolean
}) {
  const scale = useRef(new Animated.Value(1)).current
  const pulseAnim = useRef(new Animated.Value(1)).current

  useEffect(() => {
    if (!isPopular) return
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.01,
          duration: 1800,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 1800,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    ).start()
  }, [isPopular])

  return (
    <Animated.View
      style={[ocS.wrapper, isPopular && {transform: [{scale: pulseAnim}]}]}>
      <TouchableOpacity
        accessibilityRole="button"
        activeOpacity={0.92}
        onPressIn={() =>
          Animated.spring(scale, {
            toValue: 0.97,
            useNativeDriver: true,
            damping: 18,
          }).start()
        }
        onPressOut={() =>
          Animated.spring(scale, {
            toValue: 1,
            useNativeDriver: true,
            damping: 18,
          }).start()
        }
        onPress={onBuy}
        style={ocS.card}>
        <LinearGradient
          colors={offer.gradient}
          style={StyleSheet.absoluteFill}
          start={{x: 0, y: 0}}
          end={{x: 1, y: 1}}
        />
        {isPopular && (
          <Shimmer width={SW * 0.8} height={120} borderRadius={20} />
        )}

        {offer.badge && (
          <View
            style={[ocS.badge, {backgroundColor: offer.accentColor + 'dd'}]}>
            <Text style={ocS.badgeText}>{offer.badge}</Text>
          </View>
        )}

        <View style={ocS.left}>
          <Text style={ocS.emoji}>{offer.emoji}</Text>
          <View>
            <Text style={[ocS.title, {color: offer.accentColor}]}>
              {offer.title}
            </Text>
            <Text style={ocS.tagline}>{offer.tagline}</Text>
          </View>
          <View style={ocS.features}>
            {offer.features.map((f, i) => (
              <View key={i} style={ocS.featureRow}>
                <View style={[ocS.dot, {backgroundColor: offer.accentColor}]} />
                <Text style={ocS.featureText}>{f}</Text>
              </View>
            ))}
          </View>
          <Text style={ocS.social}>👥 {offer.social}</Text>
        </View>

        <View style={ocS.right}>
          <View style={[ocS.bubble, {borderColor: offer.accentColor + '55'}]}>
            <Text style={[ocS.billsNum, {color: offer.accentColor}]}>
              {offer.bills}
            </Text>
            <Text style={[ocS.billsLabel, {color: offer.accentColor}]}>
              bills
            </Text>
          </View>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 2}}>
            <DinarSymbol size={14} color="#fff" />
            <Text style={ocS.price}>{offer.priceNum}</Text>
          </View>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 1}}>
            <DinarSymbol size={8} color="rgba(255,255,255,0.35)" />
            <Text style={ocS.perBill}>
              {(offer.priceNum / offer.bills).toFixed(2)} / bill
            </Text>
          </View>
          <View style={[ocS.buyBtn, {backgroundColor: offer.accentColor}]}>
            <Text style={ocS.buyBtnText}>Acheter</Text>
            <Feather name="arrow-right" size={12} color="#fff" />
          </View>
        </View>
      </TouchableOpacity>
    </Animated.View>
  )
}

const ocS = StyleSheet.create({
  wrapper: {marginBottom: 10},
  card: {
    borderRadius: 20,
    overflow: 'hidden',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    gap: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  badge: {
    position: 'absolute',
    top: 10,
    right: 10,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  },
  badgeText: {
    color: '#fff',
    fontSize: 9,
    fontWeight: '900',
    letterSpacing: 0.5,
  },
  left: {flex: 1, gap: 7},
  emoji: {fontSize: 28},
  title: {fontSize: 18, fontWeight: '900', letterSpacing: -0.3},
  tagline: {color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 1},
  features: {gap: 4},
  featureRow: {flexDirection: 'row', alignItems: 'center', gap: 7},
  dot: {width: 4, height: 4, borderRadius: 2},
  featureText: {color: 'rgba(255,255,255,0.65)', fontSize: 11},
  social: {color: 'rgba(255,255,255,0.3)', fontSize: 10},
  right: {alignItems: 'center', gap: 6, minWidth: 80},
  bubble: {
    alignItems: 'center',
    borderWidth: 1.5,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
  billsNum: {fontSize: 26, fontWeight: '900', lineHeight: 30},
  billsLabel: {fontSize: 10, fontWeight: '800'},
  price: {color: '#fff', fontSize: 16, fontWeight: '900'},
  perBill: {color: 'rgba(255,255,255,0.35)', fontSize: 10},
  buyBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 20,
    marginTop: 2,
  },
  buyBtnText: {color: '#fff', fontSize: 12, fontWeight: '800'},
})

// ── Main sheet ────────────────────────────────────────────────────────────────
export default function BuyBillsSheet({
  visible,
  onClose,
  showDailyFreeButton,
}: Props) {
  const {
    bills,
    coins,
    addBills,
    addCoins,
    convertCoins,
    claimDailyFree,
    showToast,
  } = useBills()
  const {currentAccount} = useSession()
  const insets = useSafeAreaInsets()

  const [convertAmt, setConvertAmt] = useState('100')
  const [flashSeconds, setFlashSeconds] = useState(getFlashSecondsLeft)
  const [atipayOffer, setAtipayOffer] = useState<(typeof OFFERS)[0] | null>(
    null,
  )

  // Derive Bluesky credentials from the active session (null when not signed in)
  const currentCreds = useMemo((): AtipayCreds | null => {
    if (
      !currentAccount?.accessJwt ||
      !currentAccount?.refreshJwt ||
      !currentAccount?.did
    ) {
      return null
    }
    return {
      did: currentAccount.did,
      handle: currentAccount.handle,
      accessJwt: currentAccount.accessJwt,
      refreshJwt: currentAccount.refreshJwt,
    }
  }, [currentAccount])

  const slideAnim = useRef(new Animated.Value(SH)).current
  const backdropOp = useRef(new Animated.Value(0)).current

  useEffect(() => {
    if (!visible) return
    const id = setInterval(() => setFlashSeconds(getFlashSecondsLeft()), 1000)
    return () => clearInterval(id)
  }, [visible])

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.spring(slideAnim, {
          toValue: 0,
          useNativeDriver: true,
          damping: 22,
          stiffness: 200,
        }),
        Animated.timing(backdropOp, {
          toValue: 1,
          duration: 220,
          useNativeDriver: true,
        }),
      ]).start()
    } else {
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: SH,
          duration: 260,
          useNativeDriver: true,
        }),
        Animated.timing(backdropOp, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start()
    }
  }, [visible])

  if (!visible) return null

  const parsedConvert = parseInt(convertAmt, 10) || 0
  const billsFromConvert = Math.floor(parsedConvert / COIN_PER_BILL)
  const canConvert = billsFromConvert >= 1 && parsedConvert <= coins
  const overBalance = parsedConvert > coins

  // Demo-mode: add bills locally when no real session credentials.
  // When currentCreds is set, "Acheter" opens the real aTIProto/Stripe flow.
  const handleDemoBuy = (offer: (typeof OFFERS)[0]) => {
    addBills(offer.bills)
    showToast(`+${offer.bills} bills ajoutes (demo) 💵`, 'bill')
    setTimeout(onClose, 300)
  }

  const handleDailyFree = () => {
    claimDailyFree()
    setTimeout(onClose, 300)
  }

  const handleConvert = () => {
    if (!canConvert) return
    Keyboard.dismiss()
    const ok = convertCoins(parsedConvert)
    if (ok) {
      showToast(`+${billsFromConvert} bills obtenus ! 💵`, 'bill')
      setConvertAmt('100')
      setTimeout(onClose, 400)
    } else {
      showToast('Coins insuffisants', 'error')
    }
  }

  const quickFill = (amount: number) => {
    if (amount <= coins) setConvertAmt(String(amount))
  }

  return (
    <View style={s.overlay}>
      <Animated.View style={[s.backdrop, {opacity: backdropOp}]}>
        <Pressable
          accessibilityRole="button"
          style={StyleSheet.absoluteFill}
          onPress={onClose}
        />
      </Animated.View>

      <Animated.View
        style={[
          s.sheet,
          {
            transform: [{translateY: slideAnim}],
            paddingBottom: insets.bottom + 16,
          },
        ]}>
        {/* Handle */}
        <View style={s.handle} />

        {/* ── Hero Header ── */}
        <View style={s.heroHeader}>
          <LinearGradient
            colors={['#001f3f', '#050d18']}
            style={StyleSheet.absoluteFill}
            start={{x: 0, y: 0}}
            end={{x: 1, y: 1}}
          />
          <Shimmer width={SW} height={140} borderRadius={0} />

          <View style={s.heroContent}>
            <View style={s.heroTitleRow}>
              <Text style={s.heroEmoji}>💵</Text>
              <View style={{flex: 1}}>
                <Text style={s.heroTitle}>Capsule Bills</Text>
                <Text style={s.heroSub}>Joue plus · Apprends plus</Text>
              </View>
              <TouchableOpacity
                accessibilityRole="button"
                onPress={onClose}
                style={s.closeBtn}>
                <Feather name="x" size={18} color="#64748b" />
              </TouchableOpacity>
            </View>

            {/* Live balances */}
            <View style={s.balanceRow}>
              <View
                style={[
                  s.balanceCard,
                  {borderColor: '#0085ff44', backgroundColor: '#0085ff12'},
                ]}>
                <Text style={s.balanceEmoji}>💵</Text>
                <View>
                  <Text style={[s.balanceAmt, {color: '#0085ff'}]}>
                    {bills}
                  </Text>
                  <Text style={s.balanceLabel}>Bills</Text>
                </View>
              </View>
              <View
                style={[
                  s.balanceCard,
                  {borderColor: '#f59e0b44', backgroundColor: '#f59e0b12'},
                ]}>
                <Text style={s.balanceEmoji}>🪙</Text>
                <View>
                  <Text style={[s.balanceAmt, {color: '#f59e0b'}]}>
                    {coins}
                  </Text>
                  <Text style={s.balanceLabel}>Coins</Text>
                </View>
              </View>
              <View
                style={[
                  s.balanceCard,
                  {
                    borderColor: '#22c55e44',
                    backgroundColor: '#22c55e12',
                    flex: 1.3,
                  },
                ]}>
                <Text style={s.balanceEmoji}>🏆</Text>
                <View>
                  <Text
                    style={[s.balanceAmt, {color: '#22c55e', fontSize: 12}]}>
                    1 bill = 1 capsule
                  </Text>
                  <Text style={s.balanceLabel}>jouee</Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={s.scrollContent}
          keyboardShouldPersistTaps="handled">
          {/* ── Daily free ── */}
          {showDailyFreeButton && (
            <TouchableOpacity
              accessibilityRole="button"
              onPress={handleDailyFree}
              style={s.dailyBtn}
              activeOpacity={0.85}>
              <LinearGradient
                colors={['#052e16', '#014d25']}
                style={StyleSheet.absoluteFill}
                start={{x: 0, y: 0}}
                end={{x: 1, y: 0}}
              />
              <Text style={s.dailyIcon}>🎁</Text>
              <View style={{flex: 1}}>
                <Text style={s.dailyTitle}>Coins gratuits du jour</Text>
                <Text style={s.dailySub}>
                  Reclame +20 coins · Se renouvelle chaque jour
                </Text>
              </View>
              <View style={s.dailyBadge}>
                <Text style={s.dailyBadgeText}>GRATUIT</Text>
              </View>
            </TouchableOpacity>
          )}

          {/* ── Flash deal with real countdown ── */}
          <View style={s.flashBanner}>
            <LinearGradient
              colors={['#7a1400', '#3d0a00']}
              style={StyleSheet.absoluteFill}
              start={{x: 0, y: 0}}
              end={{x: 1, y: 0}}
            />
            <Text style={s.flashEmoji}>🔥</Text>
            <View style={{flex: 1}}>
              <Text style={s.flashTitle}>Offre de lancement</Text>
              <Text style={s.flashSub}>
                -15% sur le plan Boost cette semaine
              </Text>
            </View>
            <Countdown seconds={flashSeconds} />
          </View>

          {/* ── Section title + trust ── */}
          <View style={s.sectionHeader}>
            <Text style={s.sectionTitle}>Choisis ton plan</Text>
            <View style={s.trustRow}>
              <Text style={s.trustItem}>🔒 Securise</Text>
              <Text style={s.trustItem}>⚡ Instantane</Text>
              <Text style={s.trustItem}>🔄 Remboursable</Text>
            </View>
          </View>

          {/* ── Offer cards ── */}
          {OFFERS.map(offer => (
            <View key={offer.id}>
              <OfferCard
                offer={offer}
                onBuy={() =>
                  currentCreds ? setAtipayOffer(offer) : handleDemoBuy(offer)
                }
                isPopular={offer.id === 'boost'}
              />
              {!currentCreds && (
                <View style={s.stripeNoBluesky}>
                  <Feather name="lock" size={11} color="#475569" />
                  <Text style={s.stripeNoBlueksyText}>
                    Connecte Taderok pour activer le paiement reel via Stripe
                  </Text>
                </View>
              )}
            </View>
          ))}

          {/* ── Coin -> Bill converter ── */}
          <View style={s.convertSection}>
            <View style={s.convertHeader}>
              <Text style={s.sectionTitle}>{'Convertir Coins → Bills'}</Text>
              <View style={s.convertRate}>
                <Text style={s.convertRateText}>100 🪙 = 1 💵</Text>
              </View>
            </View>

            <Text style={s.convertBalance}>Disponible : {coins} coins</Text>

            {/* Quick fill buttons */}
            <View style={s.quickRow}>
              {[100, 200, 500].map(amt => (
                <TouchableOpacity
                  accessibilityRole="button"
                  key={amt}
                  style={[
                    s.quickBtn,
                    parsedConvert === amt && {
                      borderColor: '#0085ff',
                      backgroundColor: '#0085ff18',
                    },
                  ]}
                  onPress={() => quickFill(amt)}
                  disabled={amt > coins}>
                  <Text
                    style={[
                      s.quickBtnText,
                      amt > coins && {opacity: 0.35},
                      parsedConvert === amt && {color: '#0085ff'},
                    ]}>
                    {amt}🪙
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            <View style={s.convertRow}>
              <View
                style={[
                  s.convertInputWrap,
                  overBalance && {borderColor: '#ef444460'},
                ]}>
                <Text style={s.convertIcon}>🪙</Text>
                <TextInput
                  accessibilityLabel="Coins amount"
                  accessibilityHint="Enter the number of coins to convert into bills"
                  style={s.convertInput}
                  value={convertAmt}
                  onChangeText={setConvertAmt}
                  keyboardType="numeric"
                  placeholderTextColor="#334155"
                  placeholder="100"
                  returnKeyType="done"
                  onSubmitEditing={handleConvert}
                />
              </View>
              <View style={s.convertArrow}>
                <Feather
                  name="arrow-right"
                  size={18}
                  color={canConvert ? '#0085ff' : '#334155'}
                />
              </View>
              <View
                style={[
                  s.convertResult,
                  billsFromConvert > 0 && {borderColor: '#0085ff44'},
                ]}>
                <Text style={s.convertIcon}>💵</Text>
                <Text
                  style={[
                    s.convertResultNum,
                    {color: billsFromConvert > 0 ? '#0085ff' : '#334155'},
                  ]}>
                  {billsFromConvert}
                </Text>
                <Text style={s.convertResultLabel}>
                  bill{billsFromConvert !== 1 ? 's' : ''}
                </Text>
              </View>
            </View>

            {overBalance && (
              <Text style={s.convertError}>
                ⚠️ Solde insuffisant ({coins} coins disponibles)
              </Text>
            )}
            {!overBalance && billsFromConvert > 0 && (
              <Text style={s.convertHint}>
                Tu obtiendras {billsFromConvert} bill
                {billsFromConvert > 1 ? 's' : ''} pour {parsedConvert} coins
              </Text>
            )}

            <TouchableOpacity
              accessibilityRole="button"
              onPress={handleConvert}
              disabled={!canConvert}
              style={[s.convertBtn, !canConvert && {opacity: 0.38}]}
              activeOpacity={0.8}>
              <LinearGradient
                colors={
                  canConvert ? ['#003d7a', '#0060c0'] : ['#1e2d3d', '#1e2d3d']
                }
                style={StyleSheet.absoluteFill}
                start={{x: 0, y: 0}}
                end={{x: 1, y: 0}}
              />
              <Text style={s.convertBtnText}>
                {`Convertir ${parsedConvert > 0 ? `${parsedConvert} 🪙` : '--'} → ${billsFromConvert > 0 ? `${billsFromConvert} 💵` : '--'}`}
              </Text>
              {canConvert && (
                <Feather
                  name="check"
                  size={14}
                  color="#fff"
                  style={{marginLeft: 6}}
                />
              )}
            </TouchableOpacity>
          </View>

          {/* ── Dev button ── */}
          <TouchableOpacity
            accessibilityRole="button"
            onPress={() => {
              addCoins(200)
              showToast('+200 coins de test 🪙', 'coin')
              setTimeout(onClose, 400)
            }}
            style={s.testBtn}>
            <Feather name="terminal" size={11} color="#334155" />
            <Text style={s.testBtnText}>+200 coins (dev)</Text>
          </TouchableOpacity>
        </ScrollView>
      </Animated.View>

      {/* ── aTIProto / Stripe checkout overlay ── */}
      {atipayOffer && currentCreds && (
        <View style={s.atipayOverlay}>
          <Pressable
            accessibilityRole="button"
            style={StyleSheet.absoluteFill}
            onPress={() => setAtipayOffer(null)}
          />
          <View style={[s.atipayCard, {paddingBottom: insets.bottom + 20}]}>
            <View style={s.atipayHandle} />
            <Text style={s.atipayTitle}>Paiement Stripe securise</Text>
            <AtipaySheet
              offer={{
                id: atipayOffer.id,
                bills: atipayOffer.bills,
                priceNum: atipayOffer.priceNum,
                label: atipayOffer.title,
                emoji: atipayOffer.emoji,
                accentColor: atipayOffer.accentColor,
              }}
              creds={currentCreds}
              userId={currentAccount?.did ?? 'unknown'}
              onSuccess={newBills => {
                addBills(newBills)
                showToast(`+${newBills} bills credites ! 💵`, 'bill')
                setAtipayOffer(null)
                setTimeout(onClose, 500)
              }}
              onCancel={() => setAtipayOffer(null)}
            />
          </View>
        </View>
      )}
    </View>
  )
}

// ── Styles (dark premium overlay -- intentional) ───────────────────────────────
const s = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 999,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.75)',
  },
  sheet: {
    backgroundColor: '#060e1a',
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    maxHeight: SH * 0.93,
    overflow: 'hidden',
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.07)',
  },
  handle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#1e2d3d',
    alignSelf: 'center',
    marginTop: 10,
    marginBottom: 0,
  },

  // Hero
  heroHeader: {overflow: 'hidden'},
  heroContent: {padding: 16, gap: 14},
  heroTitleRow: {flexDirection: 'row', alignItems: 'center', gap: 12},
  heroEmoji: {fontSize: 32},
  heroTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: '900',
    letterSpacing: -0.5,
  },
  heroSub: {color: '#64748b', fontSize: 12, marginTop: 2},
  closeBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: '#0d1b2a',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#1e2d3d',
  },

  // Balance
  balanceRow: {flexDirection: 'row', gap: 8},
  balanceCard: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 10,
    borderRadius: 14,
    borderWidth: 1,
  },
  balanceEmoji: {fontSize: 18},
  balanceAmt: {fontSize: 18, fontWeight: '900', color: '#fff'},
  balanceLabel: {color: '#475569', fontSize: 10, fontWeight: '600'},

  // Scroll
  scrollContent: {padding: 14, gap: 10, paddingTop: 12},

  // Daily free
  dailyBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    borderRadius: 16,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(34,197,94,0.3)',
  },
  dailyIcon: {fontSize: 26},
  dailyTitle: {color: '#fff', fontSize: 14, fontWeight: '700'},
  dailySub: {color: '#64748b', fontSize: 11, marginTop: 2},
  dailyBadge: {
    backgroundColor: '#22c55e',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  dailyBadgeText: {color: '#fff', fontSize: 10, fontWeight: '900'},

  // Flash deal
  flashBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    borderRadius: 14,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(239,68,68,0.3)',
  },
  flashEmoji: {fontSize: 22},
  flashTitle: {color: '#fff', fontSize: 13, fontWeight: '800'},
  flashSub: {color: 'rgba(255,255,255,0.5)', fontSize: 11},

  // Section
  sectionHeader: {gap: 6},
  sectionTitle: {color: '#fff', fontSize: 15, fontWeight: '800'},
  trustRow: {flexDirection: 'row', gap: 12},
  trustItem: {color: '#475569', fontSize: 11},

  // Converter
  convertSection: {
    backgroundColor: '#0a1525',
    borderRadius: 18,
    padding: 14,
    gap: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
  },
  convertHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  convertRate: {
    backgroundColor: 'rgba(0,133,255,0.12)',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: 'rgba(0,133,255,0.25)',
  },
  convertRateText: {color: '#0085ff', fontSize: 11, fontWeight: '700'},
  convertBalance: {color: '#64748b', fontSize: 11},

  // Quick fill
  quickRow: {flexDirection: 'row', gap: 8},
  quickBtn: {
    flex: 1,
    paddingVertical: 7,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1e2d3d',
    alignItems: 'center',
    backgroundColor: '#060e1a',
  },
  quickBtnText: {color: '#64748b', fontSize: 12, fontWeight: '700'},

  convertRow: {flexDirection: 'row', alignItems: 'center', gap: 8},
  convertInputWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#060e1a',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1e2d3d',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  convertIcon: {fontSize: 16},
  convertInput: {flex: 1, color: '#fff', fontSize: 16, fontWeight: '700'},
  convertArrow: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#0d1b2a',
    alignItems: 'center',
    justifyContent: 'center',
  },
  convertResult: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: '#060e1a',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1e2d3d',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  convertResultNum: {fontSize: 18, fontWeight: '900'},
  convertResultLabel: {color: '#475569', fontSize: 12},
  convertError: {color: '#fca5a5', fontSize: 11},
  convertHint: {color: '#475569', fontSize: 11},
  convertBtn: {
    borderRadius: 14,
    overflow: 'hidden',
    padding: 14,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  convertBtnText: {color: '#fff', fontSize: 13, fontWeight: '700'},

  // Dev test
  testBtn: {
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1e2d3d',
  },
  testBtnText: {color: '#334155', fontSize: 11},

  // Stripe payment hint
  stripeNoBluesky: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: -6,
    marginBottom: 14,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  stripeNoBlueksyText: {color: '#334155', fontSize: 11},

  // AtipaySheet overlay
  atipayOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1000,
    justifyContent: 'flex-end',
  },
  atipayCard: {
    backgroundColor: '#080f1c',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 20,
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  atipayHandle: {
    width: 36,
    height: 4,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 16,
  },
  atipayTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: 16,
  },
})
